<template>
  <b-modal
    :title="$t('editor')"
    size="xl"
    modal-class="attribute-value-editor"
    header-close-content=""
    :visible="visible"
    @hidden="handleCloseModal"
    @ok="onOkButtonClicked"
    no-close-on-backdrop
    no-close-on-esc
    data-cy="attribute-value-editor-modal">
    <div class="value-editor">
      <div class="rich-editor" v-tooltip="richEditorToolTipText">
        <div v-show="!placeholderVisible()" :class="validationClass()" class="tiny-mce-container">
          <HTMLEditor
            :id="`attribute-editor-${props.language.languageCode}`"
            :ref="(x) => assignEditorRefs(x, props.language.languageCode)"
            :additional-config="tinymceConfig"
            :disabled="isReadOnly()"
            v-model="viewModel.editedValue" />
        </div>
        <div v-show="placeholderVisible()" :class="validationClass()">
          <div
            ref="placeholder"
            class="editor-placeholder"
            v-tooltip="richEditorToolTipText"
            @click.prevent="focusEditor()"
            v-dompurify-html="props.valueOrFallback.value" />
        </div>
      </div>
    </div>

    <div class="mt-2" v-if="shouldShowSuggestions() && (viewModel.suggestValues?.length || viewModel.defaultValues?.length)">
      <b-tabs v-if="!viewModel.isBusy">
        <b-tab :title="$t(getHeader('Suggest'))" v-if="!props.defaultOnly && viewModel.suggestValues && viewModel.suggestValues.length > 0">
          <div class="d-flex justify-content-end my-3">
            <common-filter v-model="viewModel.suggestFilter" :placeholder="$t('Filter')" trigger-by-time />
          </div>
          <div class="suggestions-table-container">
            <table v-if="filteredSuggestValues.length" class="suggestions-table table with-sticky-header">
              <thead>
                <tr>
                  <th class="value-header">{{ $t("value") }}</th>
                  <th class="count-header">{{ $t("amount") }}</th>
                  <th class="check-header"></th>
                </tr>
              </thead>
              <tbody class="table-body">
                <template v-for="row in filteredSuggestValues" :key="row.attributeId">
                  <tr class="table-values-row" v-if="row.value">
                    <td class="table-values-cell value-cell" :class="{ 'font-italic': !row.value }" v-tooltip="getToolTip(row.value)">
                      {{ row.value || row.fallbackValue }}
                    </td>
                    <td class="table-values-cell quantity-cell">{{ row.count > 0 ? row.count : "" }}</td>
                    <td class="table-values-cell apply-cell">
                      <b-btn
                        class="btn-icon mono"
                        @click.prevent="applySuggest(row.value)"
                        :disabled="!row.value || !canApplySuggests"
                        v-tooltip="$t('translation.applySuggestion')">
                        <i class="mdp-Check"></i>
                      </b-btn>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
            <EmptyState v-else>
              <template #default>
                <p class="m-0">
                  {{ $t("noMatches") }}<strong class="ml-1">{{ viewModel.suggestFilter }}</strong>
                </p>
              </template>
            </EmptyState>
          </div>
        </b-tab>
        <b-tab :title="$t(getHeader('Default'))" v-if="viewModel.defaultValues && viewModel.defaultValues.length > 0">
          <div class="d-flex justify-content-end my-3">
            <common-filter v-model="viewModel.defaultFilter" :placeholder="$t('Filter')" trigger-by-time />
          </div>
          <div class="suggestions-table-container">
            <table v-if="filteredDefaultValues.length" class="suggestions-table table with-sticky-header">
              <thead>
                <tr>
                  <th class="value-header">{{ $t("value") }}</th>
                  <th class="check-header"></th>
                </tr>
              </thead>
              <tbody class="table-body">
                <template v-for="row in filteredDefaultValues" :key="row.attributeId">
                  <tr class="table-values-row" v-if="row.value || row.fallbackValue">
                    <td :class="{ 'font-italic': !row.value }" class="table-values-cell value-cell" v-tooltip="getToolTip(row.value)">
                      {{ row.value || row.fallbackValue }}
                    </td>
                    <td class="table-values-cell apply-cell">
                      <b-btn
                        class="btn-icon mono"
                        @click.prevent="applySuggest(row.value)"
                        :disabled="!row.value || !canApplyDefaultValues"
                        v-tooltip="$t('translation.applySuggestion')">
                        <i class="mdp-Check"></i>
                      </b-btn>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
            <EmptyState v-else>
              <template #default>
                <p class="m-0">
                  {{ $t("noMatches") }}<strong class="ml-1">{{ viewModel.defaultFilter }}</strong>
                </p>
              </template>
            </EmptyState>
          </div>
        </b-tab>
      </b-tabs>
      <div v-else class="spinner-container">
        <Spinner />
      </div>
    </div>
    <template #modal-footer="{ ok, cancel }">
      <b-btn @click="ok" variant="primary" :disabled="(!canApplySuggests && !canApplyDefaultValues) || hasValidationError()">{{ $t("apply") }}</b-btn>
      <b-btn @click="cancel" :disabled="viewModel.isBusy">{{ $t("Close") }}</b-btn>
    </template>
  </b-modal>
</template>
<script setup lang="ts">
import CommonFilter from "@common/components/CommonFilter.vue";
import EmptyState from "@common/components/EmptyState.vue";
import HTMLEditor from "@common/components/HTMLEditor.vue";
import Spinner from "@common/components/Spinner.vue";
import { Language } from "@medapro-types/common/common";
import { RootState } from "@medapro-types/common/rootstate";
import { MasterDataTableModel } from "@medapro-types/ContentEditor/Article/MasterDataTableEditor";
import { AttributeValuesModel, DefaultAttributeUnit, DefaultAttributeValue, SuggestionsModel } from "@medapro-types/ContentEditor/content-editor-store";
import suggestApi from "@services/suggest.js";
import { NumberParser } from "@util/numberParse";
import _ from "lodash";
import moment from "moment";
import { Editor as TinymceEditor } from "tinymce";
import { ComputedRef, VNodeRef, computed, onBeforeUnmount, onMounted, reactive } from "vue";
import { useI18n } from "vue-i18n";
import { createNamespacedHelpers, useState } from "vuex-composition-helpers";
import { getLanguageDisplayName } from "../../util/languages";

//#region General
const props = defineProps<{
  editValue: any;
  visible: boolean;
  language: Language;
  dataType: string;
  field: string;
  tooltipText: string;
  valueOrFallback: any;
  attributeType: string;
  attributeId: string;
  attributes: Record<string, AttributeValuesModel>;
  canEdit: boolean;
  defaultOnly?: boolean | null;
}>();
const emit = defineEmits(["update:visible", "edited-value"]);

const placeholderVisible = () => {
  return (
    !viewModel.focused[`attribute-editor-${props.language.languageCode}`] && !props.editValue && props.valueOrFallback.isFallback && props.valueOrFallback.value
  );
};

const { t: $t } = useI18n();
//#endregion

//#region ViewModel
type ViewModel = {
  focused: Record<string, boolean>;
  editedValue: any;
  valueOrFallback: any;
  textAttributeSuggestionsEnabled: boolean;
  tableAttributeSuggestionsEnabled: boolean;
  globalAttributeSuggestionsEnabled: boolean;
  erpAttributeSuggestionsEnabled: boolean;
  suggestValues: SuggestionsModel[];
  defaultValues: Array<DefaultAttributeValue | DefaultAttributeUnit>;
  isBusy: boolean;
  suggestFilter: string;
  defaultFilter: string;
};

const viewModel = reactive<ViewModel>({
  focused: {},
  editedValue: "",
  valueOrFallback: {},
  textAttributeSuggestionsEnabled: false,
  tableAttributeSuggestionsEnabled: false,
  globalAttributeSuggestionsEnabled: false,
  erpAttributeSuggestionsEnabled: false,
  suggestValues: [],
  defaultValues: [],
  isBusy: false,
  suggestFilter: "",
  defaultFilter: ""
});

//#endregion

///article
const { useState: useArticleState } = createNamespacedHelpers("contentEditor/article");

const articleState = useArticleState(["masterdata"]) as {
  masterdata: ComputedRef<MasterDataTableModel>;
};
const masterdata = articleState.masterdata;

///userSettings
const { userSettings } = useState<RootState>(["userSettings"]);

//#region Tinymce
const richEditorToolTipText = computed(() => {
  let result = "";
  if (props.tooltipText) result += `${props.tooltipText}`;
  if (validationTooltipText.value) result += `${validationTooltipText.value}`;
  if (fallbackLanguageText.value) result += `${fallbackLanguageText.value}`;
  return result;
});
const canApplySuggests = computed(() => {
  return props.canEdit === true && !props.defaultOnly;
});
const canApplyDefaultValues = computed(() => {
  return props.canEdit === true;
});
const tinymceConfig = computed(() => {
  return {
    toolbar: getToolbar(),
    article_id: !_.isString(masterdata.value[props.language.languageCode]) ? masterdata.value[props.language.languageCode]?.articleId : "",
    language_code: props.language.languageCode,
    inherits: getInheritStatus()
  };
});

const editorRefs: Record<string, TinymceEditor> = {};

const assignEditorRefs = (el: TinymceEditor, languageCode: string): VNodeRef => {
  const key = `attribute-editor-${languageCode}`;
  editorRefs[key] = el;
  return key as VNodeRef;
};

const focusEditor = () => {
  const key = `attribute-editor-${props.language.languageCode}`;
  viewModel.focused[key] = true;
  const editor = editorRefs[key];
  editor.getEditor().focus(true);
};

const getInheritStatus = () => {
  const attributeType = props.attributeType;
  const attributes = props.attributes;
  if (!attributeType) return true;

  switch (attributeType) {
    case "invariantAttributes":
      return true;
    case "variantAttributes":
      return false;
    case "globalAttributes":
      return attributes?.[props.language.languageCode].inherits;
    default:
      return true;
  }
};

const getToolbar = () => {
  const { attributeType, field, attributes } = props;
  const languageCode = props.language.languageCode;

  const limittedToolbar =
    "fullscreen | bold italic underline | blocks | alignleft aligncenter alignright alignjustify | bullist numlist | subscript superscript | forecolor removeformat undo redo | outdent indent code | link";
  const completeToolbar = `${limittedToolbar} | smartsnippets`;

  const attributeTypesForSmartSnippets = ["invariantAttributes", "variantAttributes", "globalAttributes", "erpAttributes"];

  if (attributeTypesForSmartSnippets.includes(attributeType)) {
    if (field === "value" && !attributes?.[languageCode]?.defaultValueOnly) {
      return completeToolbar;
    } else if (field === "unit" && !attributes?.[languageCode]?.defaultUnitOnly) {
      return completeToolbar;
    }
  }

  return limittedToolbar;
};

const shouldShowSuggestions = () => {
  const attributeType = props.attributeType;
  if (!attributeType) return false;

  switch (attributeType) {
    case "invariantAttributes":
      return userSettings.value.IsTextAttributeSuggestionsEnabled == "true" || userSettings.value.IsTextAttributeSuggestionsEnabled == true;
    case "variantAttributes":
      return userSettings.value.IsTableAttributeSuggestionsEnabled == "true" || userSettings.value.IsTableAttributeSuggestionsEnabled == true;
    case "globalAttributes":
      return userSettings.value.IsGlobalAttributeSuggestionsEnabled == "true" || userSettings.value.IsGlobalAttributeSuggestionsEnabled == true;
    case "erpAttributes":
      return userSettings.value.IsERPAttributeSuggestionsEnabled == "true" || userSettings.value.IsERPAttributeSuggestionsEnabled == true;
    default:
      return false;
  }
};

const isReadOnly = () => {
  const { field, attributes, canEdit } = props;

  if (!canEdit) return true;

  const languageCode = props.language.languageCode;

  if (attributes?.[languageCode].dataType == "AttributeDataTypeLogical") {
    if (field === "value") return true;
  } else if (field === "value") return attributes?.[languageCode].defaultValueOnly;
  else if (field === "unit") return attributes?.[languageCode].defaultUnitOnly;

  return false;
};

const initialize = async () => {
  const _focused: Record<string, boolean> = {};
  const key = `attribute-editor-${props.language.languageCode}`;
  _focused[key] = false;
  viewModel.focused = _focused;
  viewModel.editedValue = props.editValue;
  viewModel.valueOrFallback = props.valueOrFallback;

  if (!shouldShowSuggestions() || !props.field || !props.attributeId) return;
  viewModel.isBusy = true;
  getDefaultValues();
  await getSuggestions();
  viewModel.isBusy = false;
};

const filteredDefaultValues = computed<Array<DefaultAttributeValue | DefaultAttributeUnit>>(() => {
  let baseList = viewModel.defaultValues;
  if (!_.isEmpty(viewModel.defaultFilter)) {
    const filter = viewModel.defaultFilter.toLocaleLowerCase();
    baseList = viewModel.defaultValues.filter((item) => item.value.toLocaleLowerCase().includes(filter));
  }

  return _.sortBy(baseList, ["sequence"]);
});

const getDefaultValues = async () => {
  const { field, attributes } = props;
  const languageCode = props.language.languageCode;
  if (field == "value" && attributes[languageCode].defaultValueOnly && attributes[languageCode].defaultValues.length > 0) {
    viewModel.defaultValues = attributes[languageCode].defaultValues;
  } else {
    await fecthDefaultValues();
  }

  if (field == "unit" && attributes[languageCode].defaultUnitOnly && attributes[languageCode].defaultUnits.length > 0) {
    viewModel.defaultValues = attributes[languageCode].defaultUnits;
  } else {
    await fecthDefaultValues();
  }
};

const fecthDefaultValues = async () => {
  try {
    viewModel.defaultValues = await suggestApi.suggest[props.attributeType][props.field].get(props.language.languageCode, props.attributeId, null, 100, true);
  } catch (error) {
    console.error("Failed to get default values for value editor", error);
  }
};

const filteredSuggestValues = computed<SuggestionsModel[]>(() => {
  let baseList = viewModel.suggestValues;
  if (!_.isEmpty(viewModel.suggestFilter)) {
    const filter = viewModel.suggestFilter.toLocaleLowerCase();
    baseList = viewModel.suggestValues.filter((item) => item.value.toLocaleLowerCase().includes(filter));
  }

  return _.sortBy(baseList, ["count", "sequence"]);
});

const getSuggestions = async () => {
  try {
    viewModel.suggestValues = await suggestApi.suggest[props.attributeType][props.field].get(props.language.languageCode, props.attributeId, null, 100, false);
  } catch (error) {
    console.error("Failed to get default values for value editor", error);
  }
};

const applySuggest = (value: string) => {
  const key = `attribute-editor-${props.language.languageCode}`;
  const editor = editorRefs[key];
  editor.getEditor().setContent(value);
  viewModel.editedValue = value;

  if (viewModel.focused[key] == false) viewModel.focused[key] = true;
};

const onOkButtonClicked = () => {
  if (!canApplySuggests.value && !canApplyDefaultValues.value) return;
  emit("edited-value", viewModel.editedValue);
  cleanupEditorInstance();
};

onMounted(() => {
  initialize();
  document.addEventListener("focusin", handleFocusIn);
});

onBeforeUnmount(() => {
  cleanupEditorInstance();
  document.removeEventListener("focusin", handleFocusIn);
});

const validationFeedback = computed(() => {
  const { field, dataType, attributes, language } = props;
  const value = viewModel.editedValue;
  let isEmptyValue = false;
  let notAllowedValue = false;
  let exceedsMaxLength = false;

  // Helper to check if the length exceeds a limit
  const lengthExceeds = (limit: number) => value && value.length > limit;

  const valueRequired = props.attributes[props.language.languageCode]?.valueRequired;
  const maxLength = props.attributes[props.language.languageCode]?.maximum;
  const maxLengthLimit = props.attributeType == "globalAttributes" ? 4000 : 1024;
  if (valueRequired && (value == null || value == "")) {
    isEmptyValue = true;
  } else if (value != null && value.length > (maxLength as number)) {
    exceedsMaxLength = true;
  }

  if (
    props.attributes[props.language.languageCode]?.defaultValueOnly &&
    props.attributes[props.language.languageCode]?.defaultValues != null &&
    props.attributes[props.language.languageCode]?.dataType != "AttributeDataTypeLogical"
  ) {
    notAllowedValue = (value && props.attributes[props.language.languageCode]?.defaultValues.every((x) => x.value != value)) as boolean;
  }

  // Helper to validate numerical types
  const isValidNumber = (parsedValue: number, originalValue: string, fractionDigits: number) =>
    originalValue === parser.parseNumberToLocaleString(parsedValue, { minimumFractionDigits: fractionDigits });
  // Check length constraints
  const msgforValue =
    (isEmptyValue &&
      $t("validation.valueRequired", {
        field: $t("value", 1)
      })) ||
    (exceedsMaxLength &&
      $t("validation.lengthExceeds", {
        field: $t("value", 1),
        length: maxLength
      })) ||
    (notAllowedValue &&
      $t("validation.mustBeDefaultValue", {
        field: $t("value", 1)
      })) ||
    "";

  if ((field === "value" || field === "attributeValue") && lengthExceeds(maxLengthLimit as number)) {
    return {
      isValid: false,
      feedback: $t("validation.lengthExceeds", { field, length: maxLengthLimit }),
      type: "error"
    };
  } else if (field === "value" || field === "attributeValue") {
    return {
      isValid: !isEmptyValue && !exceedsMaxLength && !notAllowedValue,
      feedback: msgforValue,
      type: "warning"
    };
  }

  if (field === "unit" && lengthExceeds(256)) {
    return {
      isValid: false,
      feedback: $t("validation.lengthExceeds", { field, length: 256 }),
      type: "error"
    };
  }

  const parser = new NumberParser(userSettings.value.CurrentLocale);
  const parsedValue = value && parser.parseLocaleStringToIndepentendNumberString(value);
  const fractionDigits = parsedValue && parsedValue.includes(".") ? parsedValue.split(".")[1].length : 0;

  // DataType validation
  if (dataType) {
    const parsedNumber = parser.parseLocaleStringToNumber(value);
    switch (dataType) {
      case "AttributeDataTypeNumerical": {
        if (value && !isValidNumber(parsedNumber, value, fractionDigits)) {
          return {
            isValid: false,
            feedback: $t("validation.dataTypeNotNumerical"),
            type: "warning"
          };
        }
        break;
      }
      case "AttributeDataTypeLogical": {
        const optionYes = $t("yes", 1, { locale: props.language.languageCode });
        const optionNo = $t("no", 1, { locale: props.language.languageCode });

        if (value && value !== "" && value !== optionYes && value !== optionNo) {
          return {
            isValid: false,
            feedback: $t("validation.dataTypeNotBoolean"),
            type: "warning"
          };
        }
        break;
      }
      case "AttributeDataTypeDate": {
        const format = language?.shortDateFormat?.toUpperCase() || null;
        if (value && format) {
          const isDateValid = moment(value, format).isValid();
          const isStrictlyValid = moment(value, format, true).isValid();
          if (!isDateValid) {
            return {
              isValid: false,
              feedback: `${$t("validation.dataTypeNotDate")} ${$t("validation.invalidDateFormat", { format })}`,
              type: "warning"
            };
          } else if (!isStrictlyValid) {
            return {
              isValid: false,
              feedback: `${$t("validation.invalidDateFormat", { format })} ${$t("validation.timeInfoNotSupported", { format })}`,
              type: "warning"
            };
          }
        }
        break;
      }
      case "Double":
      case "Decimal":
      case "Integer": {
        if (!value || !isValidNumber(parsedNumber, value, fractionDigits)) {
          return {
            isValid: false,
            feedback: $t(`validation.dataTypeNot${dataType}`),
            type: "warning"
          };
        }
        break;
      }
      default:
        break;
    }
  }

  if (
    field === "value" &&
    attributes &&
    attributes[language?.languageCode].defaultValueOnly &&
    attributes[language?.languageCode].defaultValues &&
    attributes[language?.languageCode].defaultValues.length > 0
  ) {
    if (value !== "" && attributes[language?.languageCode].defaultValues.every((x) => x.value !== value)) {
      return {
        isValid: false,
        feedback: $t("validation.mustBeDefaultValue", { field: $t("value", 1) }),
        type: "warning"
      };
    }
  }

  if (
    field === "unit" &&
    attributes &&
    attributes[language?.languageCode].defaultUnitOnly &&
    attributes[language?.languageCode].defaultUnits &&
    attributes[language?.languageCode].defaultUnits.length > 0
  ) {
    if (value !== "" && attributes[language?.languageCode].defaultUnits.every((x) => x.value !== value)) {
      return {
        isValid: false,
        feedback: $t("validation.mustBeDefaultValue", { field: $t("unit", 1) }),
        type: "warning"
      };
    }
  }

  return null;
});

const validationTooltipText = computed(() => {
  const localValidationData = validationFeedback?.value;
  const feedback = localValidationData && !localValidationData.isValid && localValidationData.feedback ? localValidationData.feedback : "";
  const isValidationFeedbackEmpty = feedback == "" || feedback === undefined ? "" : " ";
  return feedback + isValidationFeedbackEmpty;
});

const fallbackLanguageText = computed(() => {
  let fallbackLanguageText = "";
  if (userSettings.value.IsFallbackHintVisible) {
    fallbackLanguageText =
      props.valueOrFallback.isFallback && props.valueOrFallback.value
        ? $t("FallbackLanguageHintForField", { field: $t(props.field) }) + " " + getLanguageDisplayName(props.language.fallbackLanguageCode || "") + "."
        : "";
  }
  return fallbackLanguageText;
});

const validationClass = () => {
  const localValidationData = validationFeedback.value;
  return {
    border: localValidationData && !localValidationData.isValid,
    "border-warning": localValidationData && !localValidationData.isValid && localValidationData.type === "warning",
    "border-danger": localValidationData && !localValidationData.isValid && localValidationData.type === "error"
  };
};

const hasValidationError = () => {
  const localValidationData = validationFeedback?.value;
  return !viewModel.isBusy && localValidationData && !localValidationData?.isValid && localValidationData?.type == "error";
};

const getHeader = (type: string) => {
  switch (props.field) {
    case "value":
      return `value${type}`;
    case "unit":
      return `unit${type}`;
    default:
      return "";
  }
};

const handleCloseModal = (event: any) => {
  event.preventDefault();
  cleanupEditorInstance();
  emit("update:visible", false);
};

const cleanupEditorInstance = () => {
  const key = `attribute-editor-${props.language.languageCode}`;
  const editor = editorRefs[key];
  editor?.getEditor()?.remove();
};

const handleFocusIn = (e: Event) => {
  // Prevent Bootstrap dialog from blocking focusin
  if (e) {
    if (e.target) {
      document.addEventListener("focusin", (e) => {
        if ((e.target as HTMLElement).closest(".tox-tinymce-aux, .moxman-window, .tam-assetmanager-root") !== null) {
          e.stopImmediatePropagation();
        }
      });
    }
  }
};
//#endregion
function getToolTip(value: string) {
  if (value) return value;

  if (userSettings.value.IsFallbackHintVisible === true || userSettings.value.IsFallbackHintVisible === "true")
    return `${$t("FallbackLanguageHintForField", { field: $t("value") })} ${getLanguageDisplayName(props.language.fallbackLanguageCode || "")}.`;

  return "";
}
</script>
